import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import auth from '@/common/auth';
import DigitalParametersMixin from '@/common/mixins/digitalHelper';
/* ----- mixin for controlling the Integration flow(like Header Enrichment configuration) ------

 If an integration is configured:
 - when user presses register button, the below actions/steps are executed (in order):
 registrationRedirect()
 -> getPreRedirectionAction()  [execute appropriate actions (of active integration) before redirect user]
 -> integrationRedirect() (or register1) [get the partner url from backend, if no url go to register1]
 -> redirection to partner (or register2)  [user is redirected to partner url or to register2 page]

 - when partner redirect user (with their response) to visitor (or other) page, the below step is executed:
 getIntegrationParametersCheck()

 - when user un-sub from Duel, the below action is executed (for now, only on Vodacom needed):
 getPostUnSubAction()

 So, when a new integration need to be added, its logic need to be implemented on methods:
 getPreRedirectionAction(), getIntegrationParametersCheck(), getPostUnSubAction() [if needed]
 and use them appropriately from different components (visitor, index, signup etc)
 */

const IntegrationMixin = {
  mixins: [DigitalParametersMixin],
  data () {
    return {
      showLoadingModal: false,
      HE_ERROR_CODE: false,
      HE_LOGIN_INFO: false,
      SHOW_OPERATOR_CHOICE: false,
      ERROR_REDIRECT_MESSAGE: '',
      SHOW_HE_ERROR_RESPONSE_CODE_MESSAGE: false,
      errorStatusCode: '',
      REGISTRATION_ENDPOINT: 'api/getRegisterUrl',
    };
  },
  methods: {
    ...mapActions('common', [
      'getDecodedStatus',
      'getUserExistsInCore',
      'getUserIsRegistered',
      'updateIntegrationInfo',
      'reOptinProcess',
      'storeOperator',
    ]),
    ...mapMutations('common', [
      'setUserLanguage',
      'updateRefUrl',
      'updateCampaignId',
      'updatePublisherId',
      'storeClickid',
      'setShowSelectOperatorModal',
      'facebookReferral',
      'setOneTimeSubscriptionBundleId',
    ]),
    ...mapMutations('profile', [
      'setRegistrationInfo',
      'setFirstTimeEntry',
    ]),
    ...mapActions('profile', [
      'getUserLoginDetails',
      'storeLoginInfo',
    ]),
    ...mapActions('i18n', [
      'updateUserLanguage',
    ]),
    registrationRedirect () {
      this.getPreRedirectionAction()();
    }, // 1st Step
    getPreRedirectionAction () {
      switch (this.integrationHeader) {
        case this.availableIntegrations.ORANGE_EGYPT.name:
          return () => {
            this.integrationRedirect();
          };
        case this.availableIntegrations.VODACOM.name:
          return () => {
            this.$axios.get('api/getUsageAuthRateUrl').then((res) => {
              if (res.data.responseEnum === 'OPERATION_SUCCESS') {
                const parsedResponse = JSON.parse(res.data.data);
                if (parsedResponse.result === 'UNSUB') { // if user unsub to vodacom
                  this.setActiveIntegrationInfo([{ key: 'packageId', value: parsedResponse.packageId }]);
                  this.integrationRedirect();
                } else { // if user already sub to vodacom
                  this.$axios.get(`api/getStatus?status=7&lang=${ this.getLocale }&clickid=${ this.getClickId || '' }&refUrl=${ this.getRefUrl || '' }&publisherId=${ this.getPublisherId || '' }&campaignId=${ this.getCampaignId || '' }`).then((response) => {
                    const statusResult = response.data.data[0];
                    if (statusResult === 'ALREADY_REGISTERED') {
                      this.HE_LOGIN_INFO = true;
                    } else if (statusResult === 'SUCCESS') { // success case
                      this.setRegistrationInfo(response.data.data[1]);
                      this.$router.push('register3');
                    } else {
                      this.$router.push('/');
                    }
                  });
                }
              } else if (res.data.data === 'NO_TOKEN') this.HE_ERROR_CODE = true; // server error case
            });
          };
        case this.availableIntegrations.VIVA_KUWAIT.name:
          return () => {
            this.setActiveIntegrationInfo([{ key: 'lang', value: this.getLocale }]);
            this.integrationRedirect();
          };
        case this.availableIntegrations.DU_ETISALAT.name:
          return () => {
            if (this.multiOperatorInfo.multiOperatorEnabled) {
              this.toggleSelectOperatorModal(true);
            } else {
              this.$router.push('register1');
            }
          };
        case this.availableIntegrations.ASIACELL_IRAQ.name:
          return () => {
            if (this.multiOperatorInfo.multiOperatorEnabled) {
              this.toggleSelectOperatorModal(true);
            } else {
              this.$router.push('register1');
            }
          };
        case this.availableIntegrations.CROSS_IRAQ.name:
          return () => {
            if (this.multiOperatorInfo.multiOperatorEnabled) {
              this.toggleSelectOperatorModal(true);
            } else {
              this.$router.push('register1');
            }
          };
        case this.availableIntegrations.KSA_MOBILY.name:
          return () => {
            if (this.multiOperatorInfo.multiOperatorEnabled) {
              this.toggleSelectOperatorModal(true);
            } else {
              this.$router.push('register1');
            }
          };
        case this.availableIntegrations.OOREDOO_OMAN_V2.name:
          return () => {
            window.location.href = `${ window.location.origin }/publicapi/heCheckNewRedirection?refUrl=${ this.getRefUrl || '' }&clickid=${ this.getClickId || '' }&publisherId=${ this.getPublisherId || '' }&campaignId=${ this.getCampaignId || '' }&friendRef=${ this.fbReferral || '' }`;
          };
        default:
          return () => {
            this.$router.push('register1');
          };
      }
    }, // 2nd Step
    integrationRedirect () {
      const activeIntegration = this.getIntegrationByName();
      let url = this.REGISTRATION_ENDPOINT;
      const numOfParameters = activeIntegration.value.info.length;
      if (numOfParameters) url = `${ url }?`;
      activeIntegration.value.info.forEach((element, index) => {
        url = `${ url }${ element.key }=${ element.value }`;
        if (index + 1 !== numOfParameters) url = `${ url }&`;
      });
      this.storeParametersToBrowserStorage();
      if (activeIntegration.value.storeDigitalParams) {
        url = `${ url }&refUrl=${ this.getRefUrl || '' }&clickid=${ this.getClickId || '' }&publisherId=${ this.getPublisherId || '' }&campaignId=${ this.getCampaignId || '' }&friendRef=${ this.fbReferral || '' }`;
      }
      this.$axios.get(url).then((response) => {
        const responseEnum = response.data.responseEnum;
        let redirectionUrl = response.data.data;
        if (responseEnum === 'OPERATION_SUCCESS') {
          this.toggleSelectOperatorModal(false);
          if (redirectionUrl && redirectionUrl.startsWith('http')) {
            if (activeIntegration.value.transferDigitalParams) {
              redirectionUrl = `${ redirectionUrl }&refUrl=${ this.getRefUrl || '' }&clickid=${ this.getClickId || '' }&publisherId=${ this.getPublisherId || '' }&campaignId=${ this.getCampaignId || '' }`;
            }
            window.location.href = redirectionUrl;
          } else {
            sessionStorage.setItem('msisdn', redirectionUrl);
            this.$router.push('register2');
          }
        } else {
          this.ERROR_REDIRECT_MESSAGE = responseEnum;
        }
      });
    }, // 3rd Step
    getIntegrationParametersCheck () {
      switch (this.integrationHeader) {
        case this.availableIntegrations.VODACOM.name:
          return (to) => {
            if (to.query.result && to.query['status-code']) {
              if (to.query.result === 'ACCEPTED' || to.query.result === 'ERROR') { // ERROR accepted in case of vodacom already registered
                const params = JSON.parse(sessionStorage.getItem('params') || JSON.stringify(''));
                if (params) {
                  const clickid = params.clickid;
                  this.storeClickid({ clickid });
                  const refUrl = params.refUrl;
                  this.updateRefUrl({ refUrl });
                  const campaignId = params.campaignId;
                  this.updateCampaignId({ campaignId });
                  const publisherId = params.publisherId;
                  this.updatePublisherId({ publisherId });
                }
                this.$axios.get(`api/getStatus?status=${ to.query['status-code'] }&correlationId=${ to.query['client-txn-id'] }&lang=${ this.getLocale }&clickid=${ this.getClickId || '' }&refUrl=${ this.getRefUrl || '' }&publisherId=${ this.getPublisherId || '' }&campaignId=${ this.getCampaignId || '' }`).then((response) => {
                  const statusResult = response.data.data[0];
                  if (statusResult === 'ALREADY_REGISTERED') {
                    this.HE_LOGIN_INFO = true;
                  } else if (statusResult === 'SUCCESS') { // success case
                    this.setRegistrationInfo(response.data.data[1]);
                    this.$router.push('register3');
                  } else {
                    if (to.query['status-code'] !== '0' && to.query['status-code'] !== '7') {
                      this.errorStatusCode = to.query['status-code'];
                      this.SHOW_HE_ERROR_RESPONSE_CODE_MESSAGE = true;
                    }
                    this.$router.push('/');
                  }
                });
              } else {
                this.errorStatusCode = to.query['status-code'];
                this.SHOW_HE_ERROR_RESPONSE_CODE_MESSAGE = true;
              }
            }
          };
        case this.availableIntegrations.ORANGE_EGYPT.name:
          return (to) => {
            if (to.query.msisdn && to.query.status) {
              // eslint-disable-next-line
              const msisdn = to.query.msisdn;
              this.getDecodedStatus(to.query.status).then((res) => {
                this.setRegistrationInfo(encodeURIComponent(msisdn));
                const status = res.data;
                if (status === 'SUCCESS') {
                  this.registerCheck(msisdn);
                }
              });
            }
          };
        case this.availableIntegrations.VIVA_KUWAIT.name:
          return (to) => {
            const userMsisdn = to.query.CGMSISDN;
            const userStatus = to.query.CGSTATUS;
            if (userMsisdn && userStatus) {
              this.$axios.get(`api/getStatus?status=${ userStatus }&token=${ userMsisdn }`).then((res) => {
                const statusResult = res.data;
                if (statusResult === 'FAILURE' || statusResult === 'BLACKLISTED') {
                  this.$router.push('/');
                } else if (statusResult === 'ALREADY_REGISTERED') {
                  this.$router.push('login');
                }
              });
            }
          };
        case this.availableIntegrations.DU_ETISALAT.name:
          return (to) => {
            if (this.multiOperatorInfo.multiOperatorEnabled) {
              const correlationId = to.query.correlatorId;
              const userStatus = to.query.statusCode;
              if (correlationId && userStatus) {
                this.$axios.get(`api/getStatus?status=${ userStatus }&correlationId=${ correlationId }&lang=${ this.getLocale }`).then((res) => {
                  const statusResult = res.data.data[0];
                  if (statusResult === 'FAILURE') {
                    this.$router.push('/');
                  } else if (statusResult === 'ALREADY_REGISTERED') {
                    this.$router.push('login');
                  } else if (statusResult === 'SUCCESS') {
                    this.setRegistrationInfo(res.data.data[1]);
                    this.storeOperator(res.data.data[2]);
                    this.facebookReferral({ fbRef: res.data.data[3] });
                    this.storeLoginInfo({
                      token: res.data.data[5],
                      rememberMeParameter: true,
                      username: res.data.data[6],
                    });
                    this.getUserLoginDetails().then(() => {
                      this.updateUserLanguage();
                      this.$router.push('home');
                    });
                  }
                });
              } else this.showPage(true);
              if (to.query.user_auth_token && to.query.username) {
                this.setFirstTimeEntry(true);
              }
              this.proceedToAutoLogin(to);
            }
          };
        case this.availableIntegrations.VIVA_BAHRAIN.name:
          return (to) => {
            if (to.query.user_auth_token && to.query.username) {
              this.proceedToAutoLogin(to);
            } else this.showPage(true);
          };
        default:
          return (to) => {
            this.proceedToAutoLogin(to);
            // eslint-disable-next-line
            console.log('No parameter check ( parameters: ' + to.query + ') for active integration: ' + this.integrationHeader);
          };
      }
    }, // 4th Step (when partner redirects user back to us again)
    proceedToAutoLogin (to) {
      // eslint-disable-next-line
      console.log('No parameter check ( parameters: ' + to.query.user_auth_token + ') for active integration: ' + to.query.username);
      if (to.query.user_auth_token && to.query.username) {
        this.storeLoginInfo({
          token: to.query.user_auth_token,
          rememberMeParameter: true,
          username: to.query.username,
        });
        this.getUserLoginDetails().then(() => {
          this.updateUserLanguage();
          this.$router.push('home');
        });
      }
    },
    getAlternateRequestPinAction (isDailySubscription) {
      switch (this.integrationHeader) {
        case this.availableIntegrations.KSA_MOBILY.name:
          return (msisdn, operator) => {
            const userMsisdn = msisdn || this.user.msisdn;
            const userOperator = operator || this.operatorPicked;
            this.$axios.get(`publicapi/requestPinForTimWe?msisdn=${ userMsisdn }&operator=${ userOperator }&lang=${ this.getLocale }&clickid=${ this.getClickId }&refUrl=${ this.getRefUrl }&campaignId=${ this.getCampaignId }`).then((resp) => {
              if (resp.data.responseEnum === 'OPERATION_SUCCESS') {
                sessionStorage.setItem('msisdn', userMsisdn);
                sessionStorage.setItem('operator', userOperator);
                this.toggleSelectOperatorModal(false);
                this.$router.push('/register2');
              } else if (resp.data.responseEnum === 'ALREADY_REGISTERED') {
                this.errors = true;
                this.ERROR_REDIRECT_MESSAGE = 'ALREADY_REGISTERED';
              } else if (resp.data.responseEnum === 'MSISDN_INVALID') {
                this.errors = true;
                this.ERROR_REDIRECT_MESSAGE = 'MSISDN_INVALID';
              } else {
                this.errors = true;
                this.ERROR_REDIRECT_MESSAGE = 'SERVER_ERROR';
              }
              this.mapErrorMessages(resp.data.responseEnum);
            });
          };
        case this.availableIntegrations.ASIACELL_IRAQ.name:
          return (msisdn, operator) => {
            const userMsisdn = msisdn || this.user.msisdn;
            const userOperator = operator || this.operatorPicked;
            this.$axios.get(`api/requestPinInApp?msisdn=${ userMsisdn }&operator=${ userOperator }&lang=${ this.getLocale }`).then((res) => {
              let response = res.data.responseEnum;
              if (response == null) {
                response = res.data.data;
              } else if (response === 'OPERATION_SUCCESS') {
                this.toggleSelectOperatorModal(false);
                sessionStorage.setItem('msisdn', userMsisdn);
                this.$router.push('/register2');
              } else if (response === 'ALREADY_REGISTERED' || response === 'ALREADY_SUB_USER') {
                this.errors = true;
                this.ERROR_REDIRECT_MESSAGE = 'ALREADY_REGISTERED';
              } else if (response === 'MSISDN_INVALID') {
                this.errors = true;
                this.ERROR_REDIRECT_MESSAGE = 'MSISDN_INVALID';
              } else {
                this.errors = true;
                this.ERROR_REDIRECT_MESSAGE = 'SERVER_ERROR';
              }
              this.mapErrorMessages(response);
            });
          };
        case this.availableIntegrations.CROSS_IRAQ.name:
          return (msisdn, operator) => {
            const userMsisdn = msisdn || this.user.msisdn;
            const userOperator = operator || this.operatorPicked;
            this.$axios.get(`api/requestPinInApp?msisdn=${ userMsisdn }&operator=${ userOperator }&lang=${ this.getLocale }`).then((res) => {
              let response = res.data.responseEnum;
              if (response == null) {
                response = res.data.data;
              } else if (response === 'OPERATION_SUCCESS') {
                this.toggleSelectOperatorModal(false);
                sessionStorage.setItem('msisdn', userMsisdn);
                this.$router.push('/register2');
              } else if (response === 'ALREADY_REGISTERED' || response === 'ALREADY_SUB_USER') {
                this.errors = true;
                this.ERROR_REDIRECT_MESSAGE = 'ALREADY_REGISTERED';
              } else if (response === 'MSISDN_INVALID') {
                this.errors = true;
                this.ERROR_REDIRECT_MESSAGE = 'MSISDN_INVALID';
              } else {
                this.errors = true;
                this.ERROR_REDIRECT_MESSAGE = 'SERVER_ERROR';
              }
              this.mapErrorMessages(response);
            });
          };
        case this.availableIntegrations.OOREDOO_OMAN_V2.name:
        case this.availableIntegrations.OMANTEL_OMAN.name:
          return () => {
            this.message = '';
            this.waitForResponse = true;
            const path = `publicapi/requestPinIntegration?msisdn=${ this.user.msisdn }&lang=${ this.getLocale }&operator=${ this.operatorPicked }&isDefaultSubscription=${ isDailySubscription }`;
            this.$axios.get(path).then((response) => {
              const resp = response.data.responseEnum;
              if (resp === 'OPERATION_SUCCESS') {
                this.user.msisdn = '';
                this.setRegistrationInfo(response.data.data[0]);
                sessionStorage.setItem('msisdn', response.data.data[0]);
                this.setOneTimeSubscriptionBundleId(response.data.data[1]);
                this.storeOperator(this.operatorPicked);
                this.$router.push('register2');
                this.waitForResponse = false;
              } else {
                this.mapErrorMessages(resp);
                this.hasErrors = true;
                this.waitForResponse = false;
              }
            });
          };
        case this.availableIntegrations.CONGO_MTN.name:
          return (msisdn, operator) => {
            const userMsisdn = msisdn || this.user.msisdn;
            const userOperator = operator || this.operatorPicked;
            this.$axios.get(`api/requestPinInApp?msisdn=${ userMsisdn }&operator=${ userOperator }&lang=${ this.getLocale }&clickid=${ this.getClickId }&refUrl=${ this.getRefUrl }&campaignId=${ this.getCampaignId }&digital=true`).then((resp) => {
              if (resp.data.responseEnum === 'OPERATION_SUCCESS') {
                sessionStorage.setItem('msisdn', userMsisdn);
                sessionStorage.setItem('operator', userOperator);
                this.toggleSelectOperatorModal(false);
                this.$router.push('/register2');
              } else if (resp.data.responseEnum === 'ALREADY_REGISTERED') {
                this.errors = true;
                this.ERROR_REDIRECT_MESSAGE = 'ALREADY_REGISTERED';
              } else if (resp.data.responseEnum === 'MSISDN_INVALID') {
                this.errors = true;
                this.ERROR_REDIRECT_MESSAGE = 'MSISDN_INVALID';
              } else {
                this.errors = true;
                this.ERROR_REDIRECT_MESSAGE = 'SERVER_ERROR';
              }
              this.mapErrorMessages(resp.data.responseEnum);
            });
          };
        default:
          return null;
      }
    },
    getAlternateVerifyPinAction (ti, ts) {
      switch (this.integrationHeader) {
        /* eslint-disable */
        // case this.availableIntegrations.DU_ETISALAT.name: return () => {
        //   this.$axios.get(`api/verifyPinInApp?msisdn=${this.registrationInfoMsisdn}&pin=${this.user.pin}&lang=${this.getLocale}&operator=${this.operator}&webFlow=true`).then((response) => {
        //     const resp = response.data.responseEnum;
        //     this.waitForResponse = false;
        //     this.showLoadingModal = true;
        //     if (resp === 'OPERATION_SUCCESS') {
        //       this.$axios.get(`api/getStatusForSubDelay?msisdn=${this.registrationInfoMsisdn}&pin=${this.user.pin}&lang=${this.getLocale}`).then((res) => {
        //         this.showLoadingModal = false;
        //         if (res.data.responseEnum === 'OPERATION_SUCCESS' && res.data.data) {
        //           const hasSub = res.data.data.isSub;
        //           if (hasSub === 'true') {
        //             this.sendCompleteRegistrationEvent();
        //           }
        //           this.storeLoginInfo({
        //             token: res.data.data.user_auth_token,
        //             rememberMeParameter: false,
        //             username: res.data.data.username,
        //           });
        //           this.getUserLoginDetails().then(() => {
        //             this.setFirstTimeEntry(true);
        //             this.updateUserLanguage();
        //             this.$router.push('home');
        //           });
        //         } else { // error case
        //           this.$router.push('/');
        //         }
        //       });
        //     } else {
        //       if (resp === 'PIN_EXPIRED') {
        //         this.message = 'visitor_sign_up_2_pin_expired';
        //       } else if (resp === 'WRONG_PIN') {
        //         this.message = 'visitor_sign_up_2_warning_msg_wrong_verification_code';
        //       } else if (resp === 'BLOCKED_USER') {
        //         this.message = 'visitor_forgot_password_warning_msd_max_attempts';
        //       } else if (resp === 'SERVER_ERROR') {
        //         this.message = 'home_profile_profile_edit_server_unvailable';
        //       } else if ((resp === 'THIRD_PARTY_PIN_REQUEST_FAILED') || (resp === 'THIRD_PARTY_PIN_VERIFICATION_FAILED') || (resp === 'THIRD_PARTY_SUBSCRIPTION_PIN_REQUEST_FAILED')) {
        //         this.message = 'visitor_sign_up_warning_third_party_error';
        //       } else if (resp === 'TEST_MSISDN_ACTION_PERFOMED') {
        //         this.message = 'registration_process_with_test_msisdn_perfomed';
        //       }
        //       this.showLoadingModal = false;
        //       this.hasErrors = true;
        //     }
        //   });
        // };
        /* eslint-enable */
        case this.availableIntegrations.KSA_MOBILY.name:
          return () => {
            this.$axios.get(`publicapi/verifyPinForTimWe?msisdn=${ sessionStorage.getItem('msisdn') }&pin=${ this.user.pin }&clickid=${ this.getClickId }&refUrl=${ this.getRefUrl }&campaignId=${ this.getCampaignId }`).then((response) => {
              const resp = response.data.responseEnum;
              this.waitForResponse = false;
              this.showLoadingModal = false;
              if (resp === 'OPERATION_SUCCESS') {
                this.$router.push('login');
              } else {
                if (resp === 'PIN_EXPIRED') {
                  this.message = 'visitor_sign_up_2_pin_expired';
                } else if (resp === 'WRONG_PIN') {
                  this.message = 'visitor_sign_up_2_warning_msg_wrong_verification_code';
                } else if (resp === 'BLOCKED_USER') {
                  this.message = 'visitor_forgot_password_warning_msd_max_attempts';
                } else if (resp === 'SERVER_ERROR') {
                  this.message = 'home_profile_profile_edit_server_unvailable';
                } else if ((resp === 'THIRD_PARTY_PIN_REQUEST_FAILED') || (resp === 'THIRD_PARTY_PIN_VERIFICATION_FAILED') || (resp === 'THIRD_PARTY_SUBSCRIPTION_PIN_REQUEST_FAILED')) {
                  this.message = 'visitor_sign_up_warning_third_party_error';
                } else if (resp === 'TEST_MSISDN_ACTION_PERFOMED') {
                  this.message = 'registration_process_with_test_msisdn_perfomed';
                }
                this.showLoadingModal = false;
                this.hasErrors = true;
              }
            });
          };
        case this.availableIntegrations.ASIACELL_IRAQ.name:
          return () => {
            this.$axios.get(`api/verifyPinInApp?msisdn=${ sessionStorage.getItem('msisdn') }&pin=${ this.user.pin }&lang=${ this.getLocale }&clickid=${ this.getClickId }&refUrl=${ this.getRefUrl }&campaignId=${ this.getCampaignId }&publisherId=${ this.getPublisherId }&webflow=true`).then((res) => {
              let resp = res.data.responseEnum;
              if (resp == null) {
                resp = res.data.data;
              }
              this.waitForResponse = false;
              this.showLoadingModal = true;
              if (resp === 'OPERATION_SUCCESS') {
                this.$axios.get(`api/getStatus?msisdn=${ sessionStorage.getItem('msisdn') }&pin=${ this.user.pin }&lang=${ this.getLocale }`).then((response) => {
                  this.showLoadingModal = false;
                  if (response.data.responseEnum === 'OPERATION_SUCCESS' && response.data.data) {
                    const hasSub = response.data.data.isSub;
                    if (hasSub === 'true') {
                      this.sendCompleteRegistrationEvent();
                    }
                    this.storeLoginInfo({
                      token: response.data.data.user_auth_token,
                      rememberMeParameter: false,
                      username: res.data.data.username,
                    });
                    this.getUserLoginDetails().then(() => {
                      this.updateUserLanguage();
                      this.$router.push('home');
                    });
                  } else { // error case
                    this.$router.push('/');
                  }
                });
              } else if (resp === 'ALREADY_REGISTERED') {
                this.errors = true;
                this.ERROR_REDIRECT_MESSAGE = 'ALREADY_REGISTERED';
              } else if (resp === 'MSISDN_INVALID') {
                this.errors = true;
                this.ERROR_REDIRECT_MESSAGE = 'MSISDN_INVALID';
              } else {
                this.errors = true;
                this.ERROR_REDIRECT_MESSAGE = 'SERVER_ERROR';
              }
              this.mapErrorMessages(resp);
            });
          };
        case this.availableIntegrations.CROSS_IRAQ.name:
          return () => {
            this.$axios.get(`api/verifyPinInApp?msisdn=${ sessionStorage.getItem('msisdn') }&pin=${ this.user.pin }&lang=${ this.getLocale }&clickid=${ this.getClickId }&refUrl=${ this.getRefUrl }&campaignId=${ this.getCampaignId }&publisherId=${ this.getPublisherId }&webflow=true&ti=${ ti }&ts=${ ts }`).then((res) => {
              let resp = res.data.responseEnum;
              if (resp == null) {
                resp = res.data.data;
              }
              this.waitForResponse = false;
              this.showLoadingModal = true;
              if (resp === 'OPERATION_SUCCESS') {
                this.$axios.get(`api/getStatus?msisdn=${ sessionStorage.getItem('msisdn') }&pin=${ this.user.pin }&lang=${ this.getLocale }`).then((response) => {
                  this.showLoadingModal = false;
                  if (response.data.responseEnum === 'OPERATION_SUCCESS' && response.data.data) {
                    const hasSub = response.data.data.isSub;
                    if (hasSub === 'true') {
                      this.sendCompleteRegistrationEvent();
                    }
                    this.storeLoginInfo({
                      token: response.data.data.user_auth_token,
                      rememberMeParameter: false,
                      username: res.data.data.username,
                    });
                    this.getUserLoginDetails().then(() => {
                      this.updateUserLanguage();
                      this.$router.push('home');
                    });
                  } else { // error case
                    this.$router.push('/');
                  }
                });
              } else if (resp === 'ALREADY_REGISTERED') {
                this.errors = true;
                this.ERROR_REDIRECT_MESSAGE = 'ALREADY_REGISTERED';
              } else if (resp === 'MSISDN_INVALID') {
                this.errors = true;
                this.ERROR_REDIRECT_MESSAGE = 'MSISDN_INVALID';
              } else {
                this.errors = true;
                this.ERROR_REDIRECT_MESSAGE = 'SERVER_ERROR';
              }
              this.mapErrorMessages(resp);
            });
          };
        case this.availableIntegrations.OOREDOO_OMAN_V2.name:
        case this.availableIntegrations.OMANTEL_OMAN.name:
          return () => {
            if (this.defaultDailySubButtonEnabled) {
              this.$axios.get(`publicapi/verifyPinDailySubscription?msisdn=${ this.registrationInfoMsisdn }&pin=${ this.user.pin }&lang=${ this.getLocale }&clickid=${ this.getClickId || '' }&refUrl=${ this.getRefUrl || '' }&publisherId=${ this.getPublisherId || '' }&campaignId=${ this.getCampaignId || '' }&asyncSub=true`).then((response1) => {
                const resp = response1.data.responseEnum;
                this.waitForResponse = false;
                if (resp === 'OPERATION_SUCCESS') { // proceed to authorization
                  this.showLoadingModal = true;
                  this.$axios.get(`api/getStatus?msisdn=${ sessionStorage.getItem('msisdn') }&pin=${ this.user.pin }&lang=${ this.getLocale }&clickid=${ this.getClickId || '' }&refUrl=${ this.getRefUrl || '' }&publisherId=${ this.getPublisherId || '' }&campaignId=${ this.getCampaignId || '' }`).then((res) => {
                    this.showLoadingModal = false;
                    if (res.data.responseEnum === 'OPERATION_SUCCESS' && res.data.data) {
                      const hasSub = res.data.data.isSub;
                      if (hasSub === 'true') {
                        this.sendCompleteRegistrationEvent();
                      }
                      this.storeLoginInfo({
                        token: res.data.data.user_auth_token,
                        rememberMeParameter: false,
                        username: res.data.data.username,
                      });
                      this.getUserLoginDetails().then(() => {
                        this.updateUserLanguage();
                        this.$router.push('home');
                      });
                    } else {
                      if (resp === 'PIN_EXPIRED') {
                        this.message = 'visitor_sign_up_2_pin_expired';
                      } else if (resp === 'WRONG_PIN') {
                        this.message = 'visitor_sign_up_2_warning_msg_wrong_verification_code';
                      } else if (resp === 'BLOCKED_USER') {
                        this.message = 'visitor_forgot_password_warning_msd_max_attempts';
                      } else if (resp === 'SERVER_ERROR') {
                        this.message = 'home_profile_profile_edit_server_unvailable';
                      } else if ((resp === 'THIRD_PARTY_PIN_REQUEST_FAILED') || (resp === 'THIRD_PARTY_PIN_VERIFICATION_FAILED') || (resp === 'THIRD_PARTY_SUBSCRIPTION_PIN_REQUEST_FAILED')) {
                        this.message = 'visitor_sign_up_warning_third_party_error';
                      } else if (resp === 'TEST_MSISDN_ACTION_PERFOMED') {
                        this.message = 'registration_process_with_test_msisdn_perfomed';
                      } else { // error case
                        this.$router.push('/');
                      }
                      this.showLoadingModal = false;
                      this.hasErrors = true;
                    }
                  });
                } else if (response1.data === 'OLD_PASSWORD_CHANGED') { // here user's password updated
                  this.authorizationProcess();
                } else {
                  this.showErrorMessage(resp);
                }
              });
            } else {
              this.$axios.get(`publicapi/chargeOneTimeSubscription?msisdn=${ this.registrationInfoMsisdn }&pin=${ this.user.pin }&lang=${ this.getLocale }&bundleId=${ this.oneTimeSubscriptionBundleId }&clickid=${ this.getClickId || '' }&refUrl=${ this.getRefUrl || '' }&publisherId=${ this.getPublisherId || '' }&campaignId=${ this.getCampaignId || '' }`).then((response1) => {
                const resp = response1.data.responseEnum;
                this.waitForResponse = false;
                if (resp === 'OPERATION_SUCCESS') { // proceed to authorization
                  this.proceedToAutoLogin({ query: { user_auth_token: response1.data.data.user_auth_token, username: response1.data.data.username } });
                } else if (response1.data === 'OLD_PASSWORD_CHANGED') { // here user's password updated
                  this.authorizationProcess();
                } else {
                  this.showErrorMessage(resp);
                }
              });
            }
          };
        case this.availableIntegrations.CONGO_MTN.name:
          return () => {
            this.$axios.get(`api/verifyPinInApp?msisdn=${ sessionStorage.getItem('msisdn') }&pin=${ this.user.pin }&clickid=${ this.getClickId }&refUrl=${ this.getRefUrl }&campaignId=${ this.getCampaignId }`).then((response) => {
              const resp = response.data.responseEnum;
              this.waitForResponse = false;
              if (resp === 'OPERATION_SUCCESS') {
                this.$axios.get(`api/getStatus?msisdn=${ sessionStorage.getItem('msisdn') }&pin=${ this.user.pin }&lang=${ this.getLocale }`).then((res) => {
                  if (res.data.responseEnum === 'OPERATION_SUCCESS' && res.data.data) {
                    const hasSub = res.data.data.isSub;
                    if (hasSub === 'true') { // todo maybe needs to remove check for sub
                      this.sendCompleteRegistrationEvent();
                    }
                    this.storeLoginInfo({
                      token: res.data.data.user_auth_token,
                      rememberMeParameter: false,
                      username: res.data.data.username,
                    });
                    this.getUserLoginDetails().then(() => {
                      this.updateUserLanguage();
                      this.$router.push('home');
                    });
                  } else { // error case
                    this.$router.push('/');
                  }
                });
              } else {
                if (resp === 'PIN_EXPIRED') {
                  this.message = 'visitor_sign_up_2_pin_expired';
                } else if (resp === 'WRONG_PIN' || resp === 'IncorrectPincode') {
                  this.message = 'visitor_sign_up_2_warning_msg_wrong_verification_code';
                } else if (resp === 'BLOCKED_USER') {
                  this.message = 'visitor_forgot_password_warning_msd_max_attempts';
                } else if (resp === 'SERVER_ERROR') {
                  this.message = 'home_profile_profile_edit_server_unvailable';
                } else if ((resp === 'THIRD_PARTY_PIN_REQUEST_FAILED') || (resp === 'THIRD_PARTY_PIN_VERIFICATION_FAILED') || (resp === 'THIRD_PARTY_SUBSCRIPTION_PIN_REQUEST_FAILED')) {
                  this.message = 'visitor_sign_up_warning_third_party_error';
                } else if (resp === 'TEST_MSISDN_ACTION_PERFOMED') {
                  this.message = 'registration_process_with_test_msisdn_perfomed';
                } else if (resp === 'REQUEST_IGNORED') {
                  this.message = 'visitor_sign_up_2_not_valid_transaction';
                }
                this.showLoadingModal = false;
                this.hasErrors = true;
              }
            });
          };
        case this.availableIntegrations.IRAQ_ZAIN.name:
          return () => {
            this.$axios.get(`api/verifyPin?msisdn=${ this.registrationInfoMsisdn }&pin=${ this.user.pin }&clickid=${ this.getClickId }&refUrl=${ this.getRefUrl }&campaignId=${ this.getCampaignId }`).then((response) => {
              const resp = response.data.responseEnum;
              this.waitForResponse = false;
              this.showLoadingModal = true;
              if (resp === 'OPERATION_SUCCESS') {
                this.$axios.get(`api/getStatusForBrainiac?msisdn=${ this.registrationInfoMsisdn }&pin=${ this.user.pin }&lang=${ this.getLocale }&clickid=${ this.getClickId || '' }&refUrl=${ this.getRefUrl || '' }&publisherId=${ this.getPublisherId || '' }&campaignId=${ this.getCampaignId || '' }`).then((res) => {
                  this.showLoadingModal = false;
                  if (res.data.responseEnum === 'OPERATION_SUCCESS' && res.data.data) {
                    const hasSub = res.data.data.isSub;
                    if (hasSub === 'true') {
                      this.sendCompleteRegistrationEvent();
                    }
                    this.storeLoginInfo({
                      token: res.data.data.user_auth_token,
                      rememberMeParameter: false,
                      username: res.data.data.username,
                    });
                    this.getUserLoginDetails().then(() => {
                      this.updateUserLanguage();
                      this.$router.push('home');
                    });
                  } else { // error case
                    this.$router.push('/');
                  }
                });
              } else {
                if (resp === 'PIN_EXPIRED') {
                  this.message = 'visitor_sign_up_2_pin_expired';
                } else if (resp === 'WRONG_PIN') {
                  this.message = 'visitor_sign_up_2_warning_msg_wrong_verification_code';
                } else if (resp === 'BLOCKED_USER') {
                  this.message = 'visitor_forgot_password_warning_msd_max_attempts';
                } else if (resp === 'SERVER_ERROR') {
                  this.message = 'home_profile_profile_edit_server_unvailable';
                } else if ((resp === 'THIRD_PARTY_PIN_REQUEST_FAILED') || (resp === 'THIRD_PARTY_PIN_VERIFICATION_FAILED') || (resp === 'THIRD_PARTY_SUBSCRIPTION_PIN_REQUEST_FAILED')) {
                  this.message = 'visitor_sign_up_warning_third_party_error';
                } else if (resp === 'TEST_MSISDN_ACTION_PERFOMED') {
                  this.message = 'registration_process_with_test_msisdn_perfomed';
                }
                this.showLoadingModal = false;
                this.hasErrors = true;
              }
            });
          };
        case this.availableIntegrations.MTN_RWA.name:
          return () => {
            this.$axios.get(`api/verifyPin?msisdn=${ this.registrationInfoMsisdn }&pin=${ this.user.pin }&clickid=${ this.getClickId }&refUrl=${ this.getRefUrl }&campaignId=${ this.getCampaignId }`).then((response) => {
              const resp = response.data.responseEnum;
              this.waitForResponse = false;
              this.showLoadingModal = true;
              if (resp === 'OPERATION_SUCCESS') {
                this.$axios.get(`api/getStatus?msisdn=${ sessionStorage.getItem('msisdn') }&pin=${ this.user.pin }&lang=${ this.getLocale }`).then((res) => {
                  this.showLoadingModal = false;
                  if (res.data.responseEnum === 'OPERATION_SUCCESS' && res.data.data) {
                    const hasSub = res.data.data.isSub;
                    if (hasSub === 'true') {
                      this.sendCompleteRegistrationEvent();
                    }
                    this.storeLoginInfo({
                      token: res.data.data.user_auth_token,
                      rememberMeParameter: false,
                      username: res.data.data.username,
                    });
                    this.getUserLoginDetails().then(() => {
                      this.updateUserLanguage();
                      this.$router.push('home');
                    });
                  } else { // error case
                    this.$router.push('/');
                  }
                });
              } else {
                if (resp === 'PIN_EXPIRED') {
                  this.message = 'visitor_sign_up_2_pin_expired';
                } else if (resp === 'WRONG_PIN') {
                  this.message = 'visitor_sign_up_2_warning_msg_wrong_verification_code';
                } else if (resp === 'BLOCKED_USER') {
                  this.message = 'visitor_forgot_password_warning_msd_max_attempts';
                } else if (resp === 'SERVER_ERROR') {
                  this.message = 'home_profile_profile_edit_server_unvailable';
                } else if ((resp === 'THIRD_PARTY_PIN_REQUEST_FAILED') || (resp === 'THIRD_PARTY_PIN_VERIFICATION_FAILED') || (resp === 'THIRD_PARTY_SUBSCRIPTION_PIN_REQUEST_FAILED')) {
                  this.message = 'visitor_sign_up_warning_third_party_error';
                } else if (resp === 'TEST_MSISDN_ACTION_PERFOMED') {
                  this.message = 'registration_process_with_test_msisdn_perfomed';
                }
                this.showLoadingModal = false;
                this.hasErrors = true;
              }
            });
          };
        default:
          return null;
      }
    },
    // ----------- reSub/unSub methods ----------------------------------
    alternateReSubAction () {
      switch (this.integrationHeader) {
        case this.availableIntegrations.DU_ETISALAT.name:
          return (msisdn, operator) => {
            this.$axios.get(`api/getReSubUrl?msisdn=${ msisdn }&operator=${ operator }`).then((response) => {
              window.location.href = response.data.data;
            });
          };
        case this.availableIntegrations.ASIACELL_IRAQ.name:
          return (msisdn) => {
            this.$axios.get(`api/requestPinInApp?msisdn=${ msisdn }&reoptin=true&lang=${ this.getLocale }`).then((response) => {
              // eslint-disable-next-line
              this.reSubCallback(response); // the component that want to use it, should implement it
            });
          };
        case this.availableIntegrations.CROSS_IRAQ.name:
          return (msisdn) => {
            this.$axios.get(`api/requestPinInApp?msisdn=${ msisdn }&reoptin=true&lang=${ this.getLocale }`).then((response) => {
              // eslint-disable-next-line
              this.reSubCallback(response); // the component that want to use it, should implement it
            });
          };
        case this.availableIntegrations.ROSHAN_AFG.name:
          return (msisdn) => {
            this.$axios.get(`api/requestPinInApp?msisdn=${ msisdn }&webFlow=true&lang=${ this.getLocale }`).then((response) => {
              // eslint-disable-next-line
              this.reSubCallback(response); // the component that want to use it, should implement it
            });
          };
        case this.availableIntegrations.CONGO_MTN.name:
          return (msisdn, operator) => {
            this.$axios.get(`api/requestPinInApp?msisdn=${ msisdn }&reoptin=true&lang=${ this.getLocale }`).then((response) => {
              // eslint-disable-next-line
              this.reSubCallback(response); // the component that want to use it, should implement it
            });
          };
        case this.availableIntegrations.IRAQ_ZAIN.name:
          return (msisdn) => {
            this.$axios.get(`api/requestPinInApp?msisdn=${ msisdn }&webFlow=true&lang=${ this.getLocale }`).then((response) => {
              // eslint-disable-next-line
              this.reSubCallback(response); // the component that want to use it, should implement it
            });
          };
        default:
          return () => {
            // eslint-disable-next-line
            console.log('No presub action check for active integration: ' + this.integrationHeader);
          };
      }
    },
    standardReSubAction () {
      // eslint-disable-next-line
      console.log('Resubscribe user Call');
      return this.reOptinProcess();
    },
    getAppropriateReSubAction () {
      const DuName = this.multiOperatorInfo.multiOperatorKeys.filter(item => item.includes('DU'))[0];
      const activeIntegration = this.getIntegrationByName();
      if ((activeIntegration && activeIntegration.value.hasThirdPartyReSub) && this.userDetails.operatorName === DuName) {
        this.alternateReSubAction()(this.userDetails.msisdn, this.userDetails.operatorName);
      } else if (activeIntegration && (activeIntegration.value.name === this.availableIntegrations.ROSHAN_AFG.name)) {
        this.alternateReSubAction()(this.userDetails.msisdn, this.userDetails.operatorName);
      } else if (activeIntegration && (activeIntegration.value.name === this.availableIntegrations.CONGO_MTN.name)) {
        this.alternateReSubAction()(this.userDetails.msisdn, this.userDetails.operatorName);
      } else if (activeIntegration && (activeIntegration.value.name === this.availableIntegrations.ASIACELL_IRAQ.name)) {
        this.alternateReSubAction()(this.userDetails.msisdn, this.userDetails.operatorName);
      } else if (activeIntegration && (activeIntegration.value.name === this.availableIntegrations.CROSS_IRAQ.name)) {
        this.alternateReSubAction()(this.userDetails.msisdn, this.userDetails.operatorName);
      } else if (activeIntegration && (activeIntegration.value.name === this.availableIntegrations.IRAQ_ZAIN.name)) {
        this.alternateReSubAction()(this.userDetails.msisdn);
      } else {
        this.standardReSubAction().then((response) => {
          this.reSubCallback(response); // the component that want to use it, should implement it
        });
      }
    },
    getPostReSubAction () {
      switch (this.integrationHeader) {
        case this.availableIntegrations.VODACOM.name:
          return (callback) => {
            this.$axios.get('api/getUsageAuthRateUrl').then((res) => {
              const parsedResponse = JSON.parse(res.data.data);
              if (parsedResponse.result === 'UNSUB') { // if user is un-sub to vodacom
                const packageId = parsedResponse.packageId;
                this.$axios.get(`${ this.REGISTRATION_ENDPOINT }?packageId=${ packageId }`).then((response) => {
                  const responseEnum = response.data.responseEnum;
                  let redirectionUrl = response.data.data;
                  if (responseEnum === 'OPERATION_SUCCESS' && redirectionUrl) {
                    if (callback) {
                      redirectionUrl = auth.replaceUrlParameterValue(redirectionUrl, 'partner-redirect-url', callback);
                    }
                    window.location.href = redirectionUrl;
                  }
                });
              }
            });
          };
        default:
          return () => {
            // eslint-disable-next-line
            console.log('No action needed for active integration: ' + this.integrationHeader);
          };
      }
    },
    getReSubParametersCheck () {
      switch (this.integrationHeader) {
        case this.availableIntegrations.VODACOM.name:
          return (to) => {
            if (to.query.result && to.query['status-code']) {
              if ((to.query.result === 'ACCEPTED' && to.query['status-code'] === '0') || to.query['status-code'] === '7') { // success case
                this.$router.push('/');
              } else {
                this.errorStatusCode = to.query['status-code'];
                this.SHOW_HE_ERROR_RESPONSE_CODE_MESSAGE = true;
              }
            }
          };
        default:
          return (to) => {
            // eslint-disable-next-line
            console.log('No parameter check ( parameters: ' + to.query + ') for active integration: ' + this.integrationHeader);
          };
      }
    },
    getPostUnSubAction () {
      switch (this.integrationHeader) {
        case this.availableIntegrations.VODACOM.name:
          return () => {
            this.$axios.get('api/getUsageAuthRateUrl').then((res) => {
              const parsedResponse = JSON.parse(res.data.data);
              if (parsedResponse.result === 'ALREADY_SUB') { // if user is un-sub to vodacom
                const subId = parsedResponse.subscriptionId;
                if (subId) this.$axios.get(`api/unSubUserFromPartner?subscriptionId=${ subId }`);
              }
            });
          };
        default:
          return () => {
            // eslint-disable-next-line
            console.log('No action needed for active integration: ' + this.integrationHeader);
          };
      }
    },

    // ------------- integration helper methods -----------------------------------
    getIntegrationByName (name = this.integrationHeader) {
      if (!name) {
        return '';
      }
      const integrationKey = Object.keys(this.availableIntegrations).find(key => this.availableIntegrations[key].name === name);
      return { key: integrationKey, value: this.availableIntegrations[integrationKey] };
    },
    registerCheck (msisdn) {
      return this.getUserExistsInCore(msisdn).then((response) => {
        if (response.data) {
          this.getUserIsRegistered(msisdn).then((res) => {
            if (res.data) {
              this.$router.push('login');
            } else {
              this.$router.push('register3');
            }
          });
        } else {
          this.$router.push('/');
        }
      });
    },
    setActiveIntegrationInfo (info) {
      this.updateIntegrationInfo({ name: this.getIntegrationByName().key, value: info });
    },
    toggleSelectOperatorModal (value) {
      this.setShowSelectOperatorModal({ value });
    },
    getSelectOperatorModalChoice () {
      return this.showSelectOperatorModal;
    },
    getSmsModal () {
      return this.showCustomSmsModal;
    },
    hasExternalRedirection () {
      return this.getIntegrationByName() && this.getIntegrationByName().value.hasExternalRedirection;
    },
    hidePage () {
      return this.getIntegrationByName() && this.getIntegrationByName().value.hidePage;
    },
  },
  computed: {
    ...mapState('common', [
      'oneTimeSubscriptionBundleId',
      'defaultDailySubButtonEnabled',
      'fbReferral',
      'banner',
      'integrationHeader',
      'multiOperatorInfo',
      'availableIntegrations',
      'showSelectOperatorModal',
      'showCustomSmsModal',
      'userDetails',
    ]),
    ...mapGetters('common', [
      'getClickId',
      'getRefUrl',
      'getCampaignId',
      'getPublisherId',
    ]),
    ...mapGetters('i18n', [
      'getLocale',
    ]),
  },
};

export default IntegrationMixin;



// WEBPACK FOOTER //
// ./src/common/mixins/integration.js